import { SCENE_WIDTH, TWEEN_ONCOMPLETE } from "Data/Constants";
import { HIDE_ALL_HINTS } from "Data/Topics";
import { StickerButton } from "GameObjects/StoryGame/ch4/StickerButton";
import { StickerLayer } from "GameObjects/StoryGame/ch4/StickerLayer";
import { SceneDraggable } from "GameObjects/StoryGame/SceneDraggable";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Expo, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { MathUtils } from "html-living-framework";
import { random } from "html-living-framework/lib/Utils/ArrayUtils";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { list } from "Utils/Reflection";

import { ch4_p05 } from "../../../layouts/ch4_p05/ch4_p05"

const stickerPool = [
    ["chp4_p05_sticker1",
    "chp4_p05_sticker5",],
    ["chp4_p05_sticker2",
    "chp4_p05_sticker6",],
    ["chp4_p05_sticker3",
    "chp4_p05_sticker7",],
    ["chp4_p05_sticker4",
    "chp4_p05_sticker8",],
];

export class Chapter4Page5 extends AbstractStoryState {

    readonly bg = this.addChild(new SceneSprite());
    readonly bookBottom = this.addChild(new SceneSprite());

    readonly section3bg = this.addChild(new SceneSprite());
    readonly section1bg = this.addChild(new SceneSprite());
    readonly section4bg = this.addChild(new SceneSprite());
    readonly section2bg = this.addChild(new SceneSprite());
    readonly stickerMask = this.addChild(new StickerLayer());

    readonly bookTop = this.addChild(new SceneSprite());

    readonly photoSmallPiper = this.addChild(new SceneSprite());
    readonly photoSmallAmy = this.addChild(new SceneSprite());
    readonly photoSmallTyler = this.addChild(new SceneSprite());
    readonly photoSmallOscar = this.addChild(new SceneSprite());

    readonly stickerButton = [
        this.addChild(new StickerButton()),
        this.addChild(new StickerButton()),
        this.addChild(new StickerButton()),
        this.addChild(new StickerButton()),
    ];

    readonly frameLeft = this.addChild(SceneSprite.withPrefix("ch4_p05/"));
    readonly frameRight = this.addChild(SceneSprite.withPrefix("ch4_p05/"))

    readonly arrowButton = this.addChild(new SceneSprite());

    readonly darkOverlay = this.addChild(new SceneSprite());

    readonly lightBox = this.addChild(new PIXI.Container());

    readonly photoLargeTyler = this.lightBox.addChild(new SceneSprite());
    readonly photoLargePiper = this.lightBox.addChild(new SceneSprite());
    readonly photoLargeAmy = this.lightBox.addChild(new SceneSprite());
    readonly photoLargeOscar = this.lightBox.addChild(new SceneSprite());
    readonly whiteBorder = this.lightBox.addChild(new SceneSprite());
    readonly buttonClose = this.lightBox.addChild(new SceneSprite());

    private stickerCount: number = 0

    public init(pSize: PIXI.Point, pData: any) {
        // exported layouts from Animate CC
        ch4_p05.call(this);
        this.frameLeft.visible = false;
		this.frameRight.visible = false;

        this.makeLightBoxable(this.photoSmallTyler, this.photoLargeTyler);
        this.makeLightBoxable(this.photoSmallPiper, this.photoLargePiper);
        this.makeLightBoxable(this.photoSmallAmy, this.photoLargeAmy);
        this.makeLightBoxable(this.photoSmallOscar, this.photoLargeOscar);

        this.stickerButton.forEach(this.makeStickerButton.bind(this));

        this.initLightbox();

        this.arrowButton.interactive = true;
        this.arrowButton.buttonMode = true;
        this.arrowButton.addListener(HLF.Input.Events.POINTER_TAP, () => {
            this.gotoNextState({ transition: "up" });
        })
        // start arrow button hidden
        this.arrowButton.visible = false;

        // super.init calls onResize
        super.init(pSize, pData);

        this._subscriptions.push([
            PubSub.subscribe('stick', this.onStickerDropped.bind(this)),
            PubSub.subscribe(HLF.DRAG_BEGIN, this.onDragBegin.bind(this)),
            PubSub.subscribe(HLF.DRAGGABLE_SELECTED, this.onDragBegin.bind(this)),
            PubSub.subscribe(HLF.DRAG_END, this.onDragEnd.bind(this)),
            PubSub.subscribe(HLF.DRAGGABLE_DESELECTED, this.onDragEnd.bind(this)),
        ]);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        // stretch the overlay
        this.darkOverlay.width = pSize.x;
        this.darkOverlay.height = pSize.y;
        // move arrow button left as screen gets narrower
        this.arrowButton.position.x = MathUtils.lerp(1397, 1250, ((822 - this.position.x) / (810 - 512)));
        // back button bottom left on this page
        // if (this.backButton) {
        //     this.backButton.position.x = (822 - this.position.x) + (this.backButton.width / 2);
        // }
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        Application.instance.audio.play("HH_Ch4_Scrapbook_Music", 1, true, HLF.AudioCategory.BGM);
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
        Application.instance.audio.fadeTo("HH_Ch4_Scrapbook_Music", HLF.AudioCategory.BGM, 0, 1000);
    }

    public onAnimateInComplete() {
        super.onAnimateInComplete();
        // this.stickerButton.forEach((e, i) => this.showHint(e, "pulse", 8 + i * 0.1));
    }

    protected makeLightBoxable(pSmallImage: SceneSprite, pBigImage: SceneSprite) {
        pSmallImage.interactive = true;
        pSmallImage.buttonMode = true;
        pSmallImage.hitArea = new HLF.Input.PixelPerfectHitArea(Application.instance.renderer, pSmallImage);
        pSmallImage.addListener(HLF.Input.Events.POINTER_TAP, () => {
            PubSub.publishSync("lightbox open", pBigImage);
        });

        this._subscriptions.push(PubSub.subscribe("lightbox open", (pTopic: string, pData: SceneSprite) => {
            pBigImage.visible = (pData === pBigImage);
        }));
    }

    protected initLightbox() {
        this.darkOverlay.interactive = true;
        this.darkOverlay.visible = false;

        this.buttonClose.interactive = true;
        this.buttonClose.buttonMode = true;
        this.buttonClose.addListener(HLF.Input.Events.POINTER_TAP, () => {
            PubSub.publishSync("lightbox close", this.buttonClose);
        });
        this._subscriptions.push(PubSub.subscribe("lightbox open", () => {
            this.lightBox.visible = true;
            this.darkOverlay.visible = true;
            this.darkOverlay.interactive = true;
            TweenLite.fromTo(this.darkOverlay, 1, { pixi: { alpha: 0 } }, { pixi: { alpha: 1 } });
            this.stickerButton.forEach(s => s.deselect());

            const openSound = random(["HH_Ch4_Scrapbook_Page_Turn_1_FX", "HH_Ch4_Scrapbook_Page_Turn_2_FX", "HH_Ch4_Scrapbook_Page_Turn_3_FX"]);
            Application.instance.audio.play(openSound, 1, false, HLF.AudioCategory.SFX);
        }));

        this._subscriptions.push(PubSub.subscribe("lightbox close", () => {
            this.lightBox.visible = false;
            this.darkOverlay.interactive = false;
            TweenLite.to(this.darkOverlay, 0.5, { pixi: { alpha: 0 } }).eventCallback(TWEEN_ONCOMPLETE, () => {
                this.darkOverlay.visible = false;
            });
            const closeSound = random(["HH_Ch4_Scrapbook_Page_Turn_1_FX", "HH_Ch4_Scrapbook_Page_Turn_2_FX", "HH_Ch4_Scrapbook_Page_Turn_3_FX"]);
            Application.instance.audio.play(closeSound, 1, false, HLF.AudioCategory.SFX);
        }));

        this.lightBox.visible = false;
    }

    protected makeStickerButton(pButton: SceneDraggable) {
        // const dropShadow = new DropShadowFilter(/* {alpha: 0.58, distance: 26, blur: 0.25} */);
        // pButton.filters = [dropShadow];
    }

    // called before onDragEnd
    protected onStickerDropped(pTopic: string, pData: HLF.Input.Draggable): void {
        if (pData instanceof StickerButton && pData.interactive) {
            this.stickerCount++;
            this.hideHint(pData);

            const stickerPair = stickerPool.find(s=>s[0]===pData.name);
            const nextSticker = stickerPair!.pop()!;
            const oldPos = pData.position.clone(); // center
            const bottom = oldPos.y + pData.height / 2;
            pData.setLayoutTexture(nextSticker, 0.5, 0.5);
            pData.onLayout(); // update shadow texture
            pData.position.y = bottom - pData.height / 2; // keep bottom of sticker in same position
            stickerPair!.unshift(nextSticker);
        }
    }

    // called after onStickerDropped
    protected onDragEnd(pTopic: string, pData: HLF.Input.Draggable): void {
        if (pData instanceof StickerButton && pData.interactive) {
            // reset button position and scale
            pData.visuals.position.set(0);
            pData.visuals.scale.set(1);

            // stop hint again
            this.hideHint(pData);

            if (this.stickerCount >= 2 && !this.arrowButton.visible) {
                PubSub.publishSync(HIDE_ALL_HINTS, undefined);
                this.arrowButton.FadeIn();
                this.showHint(this.arrowButton, "pulse", 8);
            }
        }
    }

    protected onDragBegin(pTopic: string, pData: HLF.Input.Draggable): void {
        if (pData instanceof StickerButton) {
            // reset sticker scale
            pData.visuals.scale.set(1);

            // stop hint pulse
            this.hideHint(pData);
        }

    }
}